import { Injectable } from '@angular/core';
import {
  HttpInterceptor,
  HttpRequest,
  HttpHandler,
  HttpEvent,
  HttpErrorResponse,
  HttpBackend,
  HttpClient
} from '@angular/common/http';
import { Observable, throwError, BehaviorSubject } from 'rxjs';
import { catchError, filter, switchMap, take } from 'rxjs/operators';
import { environment } from 'src/environments/environment';

// URLs that should never be intercepted
const SKIP_URLS = [
  '/api/usermanagement/auth/login',
  '/api/usermanagement/auth/refresh-token'
];

@Injectable()
export class AuthInterceptor implements HttpInterceptor {

  private isRefreshing = false;
  private refreshTokenSubject: BehaviorSubject<string | null> = new BehaviorSubject<string | null>(null);

  // HttpBackend bypasses all interceptors — used exclusively for the refresh call
  private backendHttp: HttpClient;

  constructor(private handler: HttpBackend) {
    this.backendHttp = new HttpClient(handler);
  }

  intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    // Skip interceptor entirely for auth endpoints
    if (SKIP_URLS.some(url => req.url.includes(url))) {
      return next.handle(req);
    }

    return next.handle(req).pipe(
      catchError((error: HttpErrorResponse) => {
        if (error.status === 401) {
          return this.handle401Error(req, next);
        }
        return throwError(() => error);
      })
    );
  }

  private handle401Error(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    // If already refreshing, queue this request until new token is ready
    if (this.isRefreshing) {
      return this.refreshTokenSubject.pipe(
        filter(token => token !== null),
        take(1),
        switchMap(token => next.handle(this.addToken(req, token!)))
      );
    }

    this.isRefreshing = true;
    this.refreshTokenSubject.next(null);

    const refreshToken = sessionStorage.getItem('refreshToken');

    if (!refreshToken) {
      this.logout();
      return throwError(() => new Error('No refresh token available'));
    }

    // Use backendHttp (bypasses interceptors) so this call is never intercepted
    return this.backendHttp.post<any>(
      `${environment.apiUrl}/api/usermanagement/auth/refresh-token`,
      { refreshToken }
    ).pipe(
      switchMap((response: any) => {
        this.isRefreshing = false;

        if (response?.success && response?.data?.idToken) {
          const newToken = response.data.idToken;
          sessionStorage.setItem('idToken', newToken);

          if (response.data.refreshToken) {
            sessionStorage.setItem('refreshToken', response.data.refreshToken);
          }

          this.refreshTokenSubject.next(newToken);
          return next.handle(this.addToken(req, newToken));
        }

        // Refresh failed — log user out
        this.logout();
        return throwError(() => new Error('Token refresh failed'));
      }),
      catchError((err) => {
        this.isRefreshing = false;
        this.logout();
        return throwError(() => err);
      })
    );
  }

  private addToken(req: HttpRequest<any>, token: string): HttpRequest<any> {
    return req.clone({
      setHeaders: {
        Authorization: `bearer ${token}`
      }
    });
  }

  private logout(): void {
    sessionStorage.clear();
    window.location.href = '/login';
  }
}